<!--
  Harvesting 2.0 — la liste des APV.

  POURQUOI CET ÉCRAN EN REMPLACE DEUX
  « APV » et « APV SUMAL » interrogeaient le MÊME endpoint et rendaient CHACUN un
  tableau de douze colonnes. Ils différaient par quatre colonnes et deux filtres,
  et se contredisaient sur le vocabulaire : le même code source `erp` s'affichait
  « ERP seul » sur l'un et « XYLO seul » sur l'autre, et l'on comptait six
  formulations pour trois valeurs. Deux écrans pour une seule question, c'est un
  écran de trop.

  Ici : une liste, des segments au lieu de trois listes déroulantes, et un
  sélecteur de groupe de colonnes. Quatre colonnes ne bougent jamais — APV,
  Parcelle, Statut, Échéance — parce que l'échéance est la colonne vertébrale du
  module et qu'elle ne doit jamais pouvoir être masquée.
-->
<template>
  <vx-card>
    <hv-section
      title="Les APV"
      sub="Toutes les parcelles connues : celles que XYLO porte en produit, celles que SUMAL porte en dépôt, et les deux à la fois. Triées par échéance.">
      <template slot="actions">
        <vs-button size="small" type="border" icon-pack="feather" icon="icon-download"
                   :disabled="!filtered.length" @click="exportXlsx">Excel</vs-button>
      </template>
    </hv-section>

    <!-- Segments -->
    <nav class="hv2-nav">
      <a v-for="s in segments" :key="s.key"
         class="hv2-nav-item" :class="{ 'is-active': segment === s.key }"
         role="button" tabindex="0"
         @click="segment = s.key" @keyup.enter="segment = s.key">
        {{ s.label }}
        <span v-if="s.count" class="hv2-nav-count"
              :style="s.key === 'all' || s.key === 'done' ? 'background:rgba(128,128,128,.12);color:inherit' : ''">
          {{ s.count }}
        </span>
      </a>
    </nav>

    <!-- Filtres -->
    <div class="vx-row mb-4 items-end">
      <div class="vx-col md:w-1/3 w-full">
        <vs-input class="w-full" icon-pack="feather" icon="icon-search"
                  label="Rechercher" placeholder="Code APV, parcelle ou județ" v-model="search" />
      </div>
      <div class="vx-col md:w-1/3 w-full">
        <vs-select v-model="columnGroup" label="Colonnes complémentaires" class="w-full">
          <vs-select-item value="exploitation" text="Exploitation — volumes et avancement" />
          <vs-select-item value="sumal" text="SUMAL — dépôt, stock et dates" />
          <vs-select-item value="economy" text="Économie — prix et réceptions" />
        </vs-select>
      </div>
      <div class="vx-col md:w-1/4 w-full">
        <vs-checkbox v-model="onlyActiveDepot" class="mt-6">Dépôt actif dans SUMAL</vs-checkbox>
      </div>
    </div>

    <p class="hv2-sec-sub mb-3">
      {{ filtered.length }} APV affiché{{ filtered.length > 1 ? 's' : '' }} sur {{ source.length }}.
      <!-- Ce que le masquage retire, écrit noir sur blanc. Un chiffre qui tombe
           de 155 à 42 sans explication se lit comme une panne. -->
      <span v-if="horizon && horizon.hidden && segment !== 'dismissed'">
        {{ horizon.hidden }} APV sans aucune activité depuis le {{ date(horizon.from) }}
        ne sont pas listés.
      </span>
      <span v-if="horizon && horizon.archived && segment !== 'dismissed'">
        {{ horizon.archived }} dépôt(s) fermé(s) depuis plus de
        {{ horizon.closed_archive_days || 90 }} jours ne sont plus listés.
      </span>
      <span v-if="horizon && horizon.dismissed && segment !== 'dismissed'">
        {{ horizon.dismissed }} écarté(s) à la main.
      </span>
      <span v-if="segment === 'encode'">
        Ces parcelles n'ont aucune réception d'achat : ni prix, ni garantie, ni échéancier.
        « Encode » ouvre la page où l'on charge le contrat : Claude le lit et prépare la
        réception, comme le bouton du mail du matin.
      </span>
      <span v-if="segment === 'dismissed'">
        Écartés à la main parce qu'ils ne portent ni aviz ni réception. Rien n'a été supprimé.
      </span>
    </p>

    <hv-table
      :rows="filtered"
      :loading="loading"
      :error="error"
      pagination
      :max-items="25"
      empty-icon="SearchIcon"
      empty-title="Aucun APV ne correspond"
      :empty-text="source.length ? 'Aucune parcelle ne correspond à ce filtre. Essayez le segment « Tous ».' : 'Aucune parcelle connue pour le moment.'">
      <template slot="thead">
        <vs-th>APV</vs-th>
        <vs-th>Parcelle</vs-th>
        <vs-th>Appariement</vs-th>
        <vs-th>Statut</vs-th>
        <vs-th>Échéance</vs-th>
        <template v-if="columnGroup === 'exploitation'">
          <vs-th class="hv2-col-n">Réceptionné</vs-th><vs-th class="hv2-col-n">Sorti</vs-th><vs-th class="hv2-col-n">Reste</vs-th>
          <vs-th class="hv2-col-n">Avancement</vs-th><vs-th class="hv2-col-n">Avize</vs-th><vs-th class="hv2-col-n">Densité kg/m³</vs-th>
        </template>
        <template v-else-if="columnGroup === 'sumal'">
          <vs-th>Dépôt</vs-th><vs-th>Județ / Localité</vs-th><vs-th>Ouverture</vs-th>
          <vs-th>Clôture / démantèlement</vs-th><vs-th class="hv2-col-n">Stock SUMAL</vs-th>
        </template>
        <template v-else>
          <vs-th class="hv2-col-n">Réceptionné</vs-th><vs-th class="hv2-col-n">RON/m³</vs-th><vs-th class="hv2-col-n">Réceptions</vs-th>
          <vs-th class="hv2-col-n">Tarif exploit.</vs-th><vs-th class="hv2-col-n">Densité</vs-th><vs-th>Fiche</vs-th>
        </template>
        <vs-th>{{ segment === 'dismissed' ? 'Écarté' : 'Anomalies' }}</vs-th>
      </template>

      <template slot="row" slot-scope="{ r }">
        <vs-td>
          <router-link :to="parcelLink(r)" class="hv2-code">{{ r.apv_code }}</router-link>
        </vs-td>
        <vs-td>
          {{ parcelName(r) }}
          <!-- Même phrase que dans le mail : elle vient du serveur (verdictFor),
               elle n'est pas réécrite ici, sinon les deux divergeraient. -->
          <div v-if="r.verdict" class="hv2-sec-sub" style="margin:2px 0 0">{{ r.verdict.text }}</div>
        </vs-td>
        <vs-td>
          <hv-pill :label="SOURCE.label(r.source)" :tone="SOURCE.tone(r.source)" :icon="SOURCE.icon(r.source)" />
        </vs-td>
        <vs-td>
          <hv-pill :label="STATUS.label(r.status)" :tone="STATUS.tone(r.status)" :icon="STATUS.icon(r.status)" />
          <!-- « Actif, ouvert le 15/09 · 97,0 m³ déclarés » : le fait SUMAL sous
               le statut XYLO, pour ne plus avoir à changer de groupe de colonnes. -->
          <div v-if="r.depot_text" class="hv2-sec-sub" style="margin:2px 0 0">{{ r.depot_text }}</div>
        </vs-td>
        <vs-td>
          <hv-deadline :days="r.days_to_expiry" :date="date(r.deadline)"
                       :permanent="!!r.permit_is_permanent" show-date />
          <vx-tooltip v-if="r.deadline_extended" title="Délai accordé par l'ocol"
                      :text="extensionNote(r)" color="dark" position="left">
            <hv-pill label="prolongé" tone="ok" icon="CalendarIcon" />
          </vx-tooltip>
        </vs-td>

        <template v-if="columnGroup === 'exploitation'">
          <!-- La réception, et non cubage_volume_m3 : ce dernier retombe sur la
               réception quand rien n'est saisi, et se faisait passer pour un
               volume autorisé qui n'existe nulle part. -->
          <vs-td class="hv2-col-n">{{ m3(r.reception_m3) }}</vs-td>
          <vs-td class="hv2-col-n"><b>{{ m3(r.m3_out) }}</b></vs-td>
          <vs-td class="hv2-col-n">{{ m3(r.remaining_m3) }}</vs-td>
          <vs-td class="hv2-col-n">{{ pct(r.extraction_rate) }}</vs-td>
          <vs-td class="hv2-col-n">{{ int(r.avize) }}</vs-td>
          <!-- La densité de ce qui est sorti, sur les seuls avize pesés : la
               couverture s'écrit dessous, pas seulement au survol — 842 kg/m³ sur
               3 courses ne se lit pas comme 842 kg/m³ sur 200. -->
          <vs-td class="hv2-col-n">
            <template v-if="r.density_kg_m3">
              {{ int(r.density_kg_m3) }}
              <div class="hv2-sec-sub" style="margin:2px 0 0">{{ int(r.avize_weighed) }} pesé(s) sur {{ int(r.avize) }}</div>
            </template>
            <span v-else class="hv2-muted">{{ DASH }}</span>
          </vs-td>
        </template>
        <template v-else-if="columnGroup === 'sumal'">
          <vs-td>
            <hv-pill v-if="r.in_sumal" :label="DEPOT_STATUS.label(r.sumal_depot_status)"
                     :tone="DEPOT_STATUS.tone(r.sumal_depot_status)" />
            <span v-else class="hv2-muted">{{ DASH }}</span>
          </vs-td>
          <vs-td>{{ placeOf(r) }}</vs-td>
          <vs-td>{{ date(r.date_opened) }}</vs-td>
          <vs-td>{{ date(r.date_closed) }}</vs-td>
          <vs-td class="hv2-col-n">
            <a v-if="r.sumal_stock_lines" href="#" @click.prevent="openStock(r)">{{ m3(r.sumal_stock_m3) }}</a>
            <span v-else class="hv2-muted">{{ DASH }}</span>
          </vs-td>
        </template>
        <template v-else>
          <vs-td class="hv2-col-n">{{ m3(r.reception_m3) }}</vs-td>
          <vs-td class="hv2-col-n">{{ rate(r.purchase_price_ron_m3 || r.reception_price_ron_m3) }}</vs-td>
          <vs-td class="hv2-col-n">{{ int(r.receptions) }}</vs-td>
          <vs-td class="hv2-col-n">{{ rate(r.logging_rate_ron_m3) }}</vs-td>
          <!-- Densité sur les seuls avize pesés. L'infobulle porte la couverture :
               sans elle, 842 kg/m³ mesuré sur 3 courses ressemble à 842 kg/m³
               mesuré sur 200. -->
          <vs-td class="hv2-col-n">
            <vx-tooltip v-if="r.density_kg_m3"
                        title="Sur quoi porte cette densité"
                        :text="`${int(r.avize_weighed)} aviz pesé(s) au pont-bascule sur ${int(r.avize)}, soit ${m3(r.m3_weighed)} m³ des ${m3(r.m3_out)} m³ sortis.`"
                        color="dark" position="left">
              <span>{{ int(r.density_kg_m3) }}</span>
            </vx-tooltip>
            <span v-else class="hv2-muted">{{ DASH }}</span>
          </vs-td>
          <vs-td>
            <hv-pill v-if="r.sheet_complete" label="complète" tone="ok" icon="CheckIcon" />
            <vx-tooltip v-else
                        title="Ce qui manque à la fiche"
                        :text="(r.sheet_gaps || []).map(g => GAP.label(g)).join(', ')"
                        color="dark" position="left">
              <hv-pill :label="`${(r.sheet_gaps || []).length} manque(s)`" tone="warn" icon="EditIcon" />
            </vx-tooltip>
          </vs-td>
        </template>

        <vs-td>
          <span class="hv2-pills">
            <!-- Dans le segment « Écartés », cette colonne sert à revenir en
                 arrière : c'est ce qui rend le geste réversible pour de vrai. -->
            <template v-if="segment === 'dismissed'">
              <vx-tooltip v-if="r.dismiss_reason" title="Motif" :text="r.dismiss_reason"
                          color="dark" position="left">
                <hv-pill label="écarté" tone="danger" icon="EyeOffIcon" />
              </vx-tooltip>
              <hv-pill v-else label="écarté" tone="danger" icon="EyeOffIcon" />
              <vs-button size="small" type="border" icon-pack="feather" icon="icon-rotate-ccw"
                         @click="restore(r)">Rétablir</vs-button>
            </template>
            <!-- Le bouton « Encode » du mail, le même partout (Chady, 2026-10-27) :
                 il remplace la popup d'encodage à la main. -->
            <hv-encode-button v-else-if="needsReception(r)" :apv="r.apv_code" />
            <!-- Chaque pastille porte sa phrase au survol : « à encoder » ou
                 « 4 produits » ne veulent rien dire pour qui n'a pas construit
                 le module. La définition vit dans format.js, une seule fois. -->
            <vx-tooltip
              v-for="a in (segment === 'dismissed' ? [] : anomalies(r))"
              :key="a.key"
              :title="a.title"
              :text="a.why"
              color="dark"
              position="left">
              <hv-pill :label="a.label" :tone="a.tone" :icon="a.icon" />
            </vx-tooltip>
            <span v-if="segment !== 'dismissed' && !anomalies(r).length && !needsReception(r)"
                  class="hv2-muted">—</span>
          </span>
        </vs-td>
      </template>
    </hv-table>

    <!-- Le stock d'un dépôt. TOUJOURS montée, jamais en v-if : Vuesax déplace la
         popup dans document.body et un v-if la détruit en laissant la page figée. -->
    <vs-popup :title="stockTitle" :active.sync="stockOpen">
      <p class="hv2-sec-sub mb-4">
        Ce que SUMAL déclare posé à ce dépôt. Instantané repris à chaque synchro,
        affiché tel quel : aucun document de l'ERP n'en découle et rien n'est réécrit vers SUMAL.
      </p>
      <hv-table
        :rows="stockRows"
        :loading="stockLoading"
        row-key="species"
        empty-icon="PackageIcon"
        empty-text="Aucun stock déclaré à ce dépôt.">
        <template slot="thead">
          <vs-th>Groupe</vs-th><vs-th>Espèce</vs-th><vs-th>Sortiment</vs-th>
          <vs-th>Sous-sortiment</vs-th><vs-th class="hv2-col-n">Volume</vs-th>
        </template>
        <template slot="row" slot-scope="{ r }">
          <vs-td>{{ r.species_group || DASH }}</vs-td>
          <vs-td>{{ r.species || DASH }}</vs-td>
          <vs-td>{{ r.assortment || DASH }}</vs-td>
          <vs-td>{{ r.sub_assortment || DASH }}</vs-td>
          <vs-td class="hv2-col-n">{{ m3(r.volume_m3) }}</vs-td>
        </template>
      </hv-table>
      <p v-if="stockRows.length" class="mt-4">
        <b>Total : {{ m3(stockTotal) }} m³</b> sur {{ stockRows.length }} lignes.
      </p>
    </vs-popup>
  </vx-card>
</template>

<script>
import axios from '@/axios.js'
import XLSX from 'xlsx-js-style'
import formatMixin from './formatMixin'
import fmt from './format'
import HvSection from './components/HvSection'
import HvPill from './components/HvPill'
import HvTable from './components/HvTable'
import HvDeadline from './components/HvDeadline'
import HvEncodeButton from './components/HvEncodeButton'

export default {
  mixins: [formatMixin],
  components: { HvSection, HvPill, HvTable, HvDeadline, HvEncodeButton },
  data () {
    return {
      loading: true,
      error: '',
      rows: [],
      // Les écartés vivent à part : les verser dans `rows` fausserait tous les
      // compteurs de segments dès qu'on ouvre leur onglet.
      dismissedRows: [],
      horizon: null,
      search: '',
      segment: 'all',
      columnGroup: 'exploitation',
      onlyActiveDepot: false,
      stockOpen: false,
      stockLoading: false,
      stockRows: [],
      stockTitle: ''
    }
  },
  computed: {
    segments () {
      const c = (fn) => this.rows.filter(fn).length
      return [
        { key: 'all', label: 'Tous', count: this.rows.length },
        { key: 'encode', label: 'Sans réception', count: c((r) => this.needsReception(r)) },
        { key: 'deadline', label: 'Échéances', count: c((r) => this.isDeadline(r)) },
        { key: 'anomaly', label: 'Anomalies', count: c((r) => this.isAnomaly(r)) },
        { key: 'done', label: 'Terminés', count: c((r) => r.status === 'done') },
        // Le seul endroit d'où un APV écarté peut être relu et rétabli. Sans
        // segment, « réversible » ne serait vrai que sur le papier.
        { key: 'dismissed', label: 'Écartés', count: this.dismissedRows.length }
      ]
    },
    // « Écartés » est un autre jeu de lignes, pas un filtre du tableau courant.
    source () { return this.segment === 'dismissed' ? this.dismissedRows : this.rows },
    filtered () {
      const q = this.search.trim().toLowerCase()
      return this.source
        .filter((r) => {
          if (this.segment === 'dismissed') return !q || this.matches(r, q)
          if (this.segment === 'encode' && !this.needsReception(r)) return false
          if (this.segment === 'deadline' && !this.isDeadline(r)) return false
          if (this.segment === 'anomaly' && !this.isAnomaly(r)) return false
          if (this.segment === 'done' && r.status !== 'done') return false
          if (this.onlyActiveDepot && r.sumal_depot_status !== 'ACTIVE') return false
          if (!q) return true
          return this.matches(r, q)
        })
        .sort((a, b) => {
          // Les chantiers vivants d'abord. Trier la liste entière par échéance
          // croissante la faisait s'ouvrir sur des parcelles closes depuis 2022,
          // les plus dépassées étant les plus anciennes : la première page ne
          // montrait que de l'archive.
          const liveA = a.status !== 'done' ? 0 : 1
          const liveB = b.status !== 'done' ? 0 : 1
          if (liveA !== liveB) return liveA - liveB

          const da = a.days_to_expiry === null ? 99999 : a.days_to_expiry
          const db = b.days_to_expiry === null ? 99999 : b.days_to_expiry
          // Vivant : ce qui expire le plus tôt en tête. Clos : le plus récent
          // d'abord, l'archive se lit de la fin vers le début.
          if (liveA === 0) { if (da !== db) return da - db }
          else if (da !== db) return db - da
          return (b.m3_out || 0) - (a.m3_out || 0)
        })
    },
    stockTotal () { return this.stockRows.reduce((s, r) => s + Number(r.volume_m3 || 0), 0) }
  },
  methods: {
    /**
     * Toutes les échéances d'un chantier vivant, et non les seules à 30 jours.
     *
     * Le plafond faisait disparaître de la liste ce qui tombe dans deux mois :
     * on ne pouvait pas préparer, seulement constater. Le tri par échéance
     * croissante met de toute façon l'urgent en tête, donc rien ne se perd à
     * montrer la suite. Un chantier terminé reste exclu : sa date est un fait
     * d'archive, pas une échéance.
     */
    isDeadline (r) {
      return r.status !== 'done' && !r.permit_is_permanent && r.days_to_expiry !== null
    },
    // Le filtre et la colonne doivent dire la même chose : une parcelle comptée
    // dans « Anomalies » qui n'affiche aucune pastille serait incompréhensible.
    isAnomaly (r) { return this.anomalies(r).length > 0 },
    openStock (r) {
      this.stockTitle = `${r.apv_code} — ${this.parcelName(r)}`
      this.stockRows = []
      this.stockOpen = true
      this.stockLoading = true
      axios.get(`/sumal2/stock/${r.apv_code}`)
        .then((res) => { this.stockRows = res.data || [] })
        .catch(() => { this.stockRows = [] })
        .finally(() => { this.stockLoading = false })
    },

    /**
     * Un seul export, en français. Il y en avait deux : l'un en anglais
     * (« Parcel », « Deadline », « YES »), l'autre en français sans accents avec
     * un « Judet » roumain. Aucun des deux ne reprenait les intitulés de l'écran.
     */
    exportXlsx () {
      const rows = this.filtered.map((r) => ({
        'Code APV': r.apv_code,
        'Parcelle': this.parcelName(r),
        'Appariement': fmt.SOURCE.label(r.source),
        'Statut': fmt.STATUS.label(r.status),
        'Statut du dépôt SUMAL': fmt.DEPOT_STATUS.label(r.sumal_depot_status),
        'Ouverture': fmt.dateISO(r.date_opened),
        'Échéance retenue': fmt.dateISO(r.deadline),
        'Autorisation': fmt.dateISO(r.permit_expiry || r.date_permit_expiry),
        'Prolongé': r.deadline_extended ? 'oui' : '',
        'Jours restants': r.days_to_expiry,
        'Clôture': fmt.dateISO(r.date_closed),
        'Județ': r.sumal_county || '',
        'Localitate': r.sumal_locality || '',
        // « Volume autorisé » n'existe nulle part : ni SUMAL ni la réception ne
        // portent cette notion. On exporte ce qu'on a vraiment.
        'Réceptionné m³': r.reception_m3,
        'Volume martelage m³': r.cubage_source === 'typed' ? r.cubage_volume_m3 : null,
        'Sorti m³': r.m3_out,
        'Reste m³': r.remaining_m3,
        'Avancement %': r.extraction_rate === null ? null : Math.round(r.extraction_rate * 100),
        'Avize': r.avize,
        'Réceptions': r.receptions,
        'Prix achat RON/m³': r.purchase_price_ron_m3 || r.reception_price_ron_m3,
        'Tarif exploitation RON/m³': r.logging_rate_ron_m3,
        'Stock SUMAL m³': r.sumal_stock_m3,
        'Densité kg/m³': r.density_kg_m3 === null ? null : Math.round(r.density_kg_m3),
        'Avize pesés': r.avize_weighed,
        'À encoder': r.not_encoded ? 'oui' : '',
        'Sans réception': r.no_purchase_contract ? 'oui' : '',
        'Fiche incomplète': r.sheet_complete ? '' : (r.sheet_gaps || []).map((g) => fmt.GAP.label(g)).join(', ')
      }))
      const ws = XLSX.utils.json_to_sheet(rows)
      // Une largeur par colonne, dans l'ordre des clés ci-dessus. Les compter à
      // la main se paie au premier ajout : on les dérive.
      ws['!cols'] = Object.keys(rows[0] || {}).map((k) => ({ wch: Math.max(11, k.length + 2) }))
      ws['!freeze'] = { xSplit: 2, ySplit: 1 }
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'APV')
      XLSX.writeFile(wb, `harvesting2-apv-${new Date().toISOString().slice(0, 10)}.xlsx`)
    },

    // La recherche couvre aussi la localité, puisque la colonne l'affiche.
    matches (r, q) {
      return (r.apv_code || '').toLowerCase().indexOf(q) !== -1 ||
             (this.parcelName(r) || '').toLowerCase().indexOf(q) !== -1 ||
             (r.sumal_county || '').toLowerCase().indexOf(q) !== -1 ||
             (r.sumal_locality || '').toLowerCase().indexOf(q) !== -1
    },
    /**
     * Un APV à encoder n'est pas seulement un APV sans produit : le robot SUMAL
     * crée le produit dès le PREMIER AVIZ, si bien que la plupart des APV à
     * encoder en portent déjà un. Ce qui manque, c'est la réception d'achat.
     */
    needsReception (r) { return Number(r.receptions) === 0 },
    placeOf (r) {
      return [r.sumal_county, r.sumal_locality].filter(Boolean).join(' / ') || this.DASH
    },

    notify (title, text, color) {
      this.$vs.notify({
        title,
        text,
        color,
        iconPack: 'feather',
        icon: color === 'danger' ? 'icon-alert-circle' : 'icon-check',
        position: 'bottom-right',
        time: 6000
      })
    },

    async restore (r) {
      try {
        await axios.post(`/harvesting2/parcel/${r.apv_code}/restore`)
        this.notify('APV rétabli', `${r.apv_code} réapparaît dans les listes.`, 'success')
        this.load()
      } catch (e) {
        this.notify('Rétablissement impossible',
          (e.response && e.response.data && e.response.data.message) || e.message, 'danger')
      }
    },

    /**
     * `?envelope=1` demande `{ rows, horizon }` : `horizon` porte combien d'APV
     * sont masqués et pourquoi, et un tableau nu ne peut pas le transporter.
     *
     * `unwrap` accepte AUSSI le tableau nu, la forme que sert une API plus
     * ancienne. Sans ça, l'écran ne marche que si l'API a été déployée d'abord —
     * et le jour où l'ordre s'inverse, la liste des APV tombe sur
     * « rows.filter is not a function ». Un écran ne doit pas dépendre de
     * l'ordre de deux rsync.
     */
    load () {
      this.loading = true
      this.error = ''
      const unwrap = (d) => {
        if (Array.isArray(d)) return { rows: d, horizon: null }
        return d || {}
      }
      // Les deux jeux en parallèle. Les écartés sont une poignée de lignes :
      // les charger d'office évite de faire clignoter le compteur du segment,
      // et de recharger à chaque va-et-vient entre les onglets.
      Promise.all([
        axios.get('/harvesting2/parcels?envelope=1'),
        axios.get('/harvesting2/parcels?envelope=1&scope=dismissed').catch(() => ({ data: [] }))
      ])
        .then(([live, dismissed]) => {
          const d = unwrap(live.data)
          this.rows = d.rows || []
          this.horizon = d.horizon || null
          // Filtré sur `dismissed_at` et pas seulement sur la réponse : une API
          // qui ne connaît pas encore `scope` ignore le paramètre et renvoie
          // TOUTES les parcelles. Sans ce filtre, l'onglet « Écartés » les
          // afficherait toutes et proposerait de les « rétablir ».
          this.dismissedRows = (unwrap(dismissed.data).rows || []).filter((r) => r.dismissed_at)
        })
        .catch((e) => {
          this.error = (e.response && e.response.data && e.response.data.message) || e.message || 'Erreur inconnue'
        })
        .finally(() => { this.loading = false })
    }
  },
  created () {
    // L'ancien onglet « APV SUMAL » redirige ici avec ?vue=sumal : le lien reste
    // valide, et il atterrit sur le segment qui remplace cet écran.
    if (this.$route.query.vue === 'sumal') this.segment = 'encode'
    this.load()
  }
}
</script>
